<template>
  <modal id="endDeviceForm"
         v-model="visible"
         :title="$t('ed-endDeviceForm:update', { endDevice: endDeviceToEdit.devEui })"
         :callback="update"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('ed-endDeviceForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <div class="form-row">
        <div class="form-group col-md-8">
          <label for="cluster" class="control-label">{{ $t('ed-endDeviceForm:cluster') | capitalize }} *</label>
          <select id="cluster" v-model="newCluster" class="form-control">
            <template v-if="isRole.superAdmin">
              <optgroup v-for="(clustersList, customer) in groupedClusters" :key="customer" :label="customer">
                <option v-for="cluster in clustersList" :key="cluster.id" :value="cluster">{{ cluster.name }}</option>
              </optgroup>
            </template>

            <template v-else>
              <option v-for="cluster in $store.getters.allClusters" :key="cluster.id" :value="cluster">{{ cluster.name }}</option>
            </template>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-8">
          <label for="name" class="control-label">{{ $t('ed-endDeviceForm:name') | capitalize }}</label>
          <input id="name"
                 v-model="name"
                 v-validate="'text'"
                 :data-vv-as="$t('ed-endDeviceForm:name')"
                 :class="{ 'is-invalid': errors.has('name') }"
                 type="text"
                 name="name"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="control-label d-block">{{ $t('ed-endDeviceForm:adr') | capitalize }}</label>
          <input id="adrEnabled"
                 v-model="adrEnabled"
                 type="checkbox"
          >
          <label for="adrEnabled" />
          <label for="adrEnabled" class="control-label">{{ $t('ed-endDeviceForm:adrEnable') | capitalize }}</label>
        </div>

        <div v-show="activation==='OTAA' && isLoRaWan10" class="form-group col-lg-3 col-md-6">
          <label class="control-label d-block">{{ $t('ed-endDeviceForm:devNonceCounter') | capitalize }}</label>
          <input id="devNonceCounter"
                 v-model="devNonceCounter"
                 type="checkbox"
          >
          <label for="devNonceCounter" />
          <label for="devNonceCounter" class="control-label">{{ $t('ed-endDeviceForm:devNonceCounterEnable') | capitalize }}</label>
        </div>
      </div>

      <h3 class="mt-3">{{ $t('ed-endDeviceForm:geolocation') | capitalize }}</h3>

      <div class="form-group">
        <label for="geolocation" class="control-label d-block">{{ $t('ed-endDeviceForm:geolocationType') | capitalize }}</label>

        <input id="geolocManual"
               v-model="geolocation"
               v-validate="'required'"
               :data-vv-as="$t('ed-endDeviceForm:geolocationType')"
               :class="{ 'is-invalid': errors.has('geolocation') }"
               type="radio"
               value="MANUAL"
               name="geolocation"
        >
        <label for="geolocManual">{{ $t('ed-endDeviceForm:geolocationManual') | capitalize }}</label>

        <input id="geolocInherited"
               v-model="geolocation"
               v-validate="'required'"
               :class="{ 'is-invalid': errors.has('geolocation') }"
               type="radio"
               value="INHERITED"
               name="geolocation"
        >
        <label for="geolocInherited">{{ $t('ed-endDeviceForm:geolocationInherited') | capitalize }} ({{ $t(newCluster.geolocEnabled ? 'enabled' : 'disabled') }})</label>

        <div class="invalid-feedback">{{ errors.first('geolocation') }}</div>
      </div>

      <div v-show="geolocation==='MANUAL'" class="form-row">
        <div class="form-group col-sm-4">
          <label class="control-label">{{ $t('ed-endDeviceForm:latitude') | capitalize }} *</label>
          <input id="latitude"
                 v-model="latitude"
                 v-validate="{rules: {required: geolocation==='MANUAL', number:true, between: [-90, 90]}}"
                 :class="{ 'is-invalid': errors.has('latitude') }"
                 type="text"
                 name="latitude"
                 class="form-control"
                 min="-90"
                 max="90"
          >
          <small class="form-text text-muted">{{ $t('ed-endDeviceForm:latitudeUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('latitude') }}</div>
        </div>

        <div class="form-group col-sm-4">
          <label class="control-label">{{ $t('ed-endDeviceForm:longitude') | capitalize }} *</label>
          <input id="longitude"
                 v-model="longitude"
                 v-validate="{rules: {required: geolocation==='MANUAL', number:true, between: [-180, 180]}}"
                 :class="{ 'is-invalid': errors.has('longitude') }"
                 type="text"
                 name="longitude"
                 class="form-control"
                 min="-180"
                 max="180"
          >
          <small class="form-text text-muted">{{ $t('ed-endDeviceForm:longitudeUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('longitude') }}</div>
        </div>

        <div class="form-group col-sm-4">
          <label class="control-label">{{ $t('ed-endDeviceForm:altitude') | capitalize }} (m) *</label>
          <input id="altitude"
                 v-model="altitude"
                 v-validate="{rules: {required: geolocation==='MANUAL', number:true, between: [-2147483648, 2147483648]}}"
                 :class="{ 'is-invalid': errors.has('altitude') }"
                 type="text"
                 name="altitude"
                 class="form-control"
                 min="-2147483648"
                 max="2147483647"
          >
          <small class="form-text text-muted">{{ $t('ed-endDeviceForm:altitudeUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('altitude') }}</div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { groupBy, orderBy, isEmpty, pick, find } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    endDeviceToEdit: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      clusters: [],
      id: '',
      newCluster: {},
      name: '',
      activation: '',
      adrEnabled: false,
      devNonceCounter: false,
      geolocation: '',
      longitude: '',
      latitude: '',
      altitude: ''
    }
  },

  computed: {
    // Group clusters by customer for superAdmin
    groupedClusters () {
      // Order by customer name
      // Group by customer name to add customer in lists
      return groupBy(orderBy(this.$store.getters.allClusters, ['customer.name']), 'customer.name')
    },

    // DevNonceCounter only for 1.0 version
    isLoRaWan10 () {
      return this.endDeviceToEdit.macVersion && this.endDeviceToEdit.macVersion.indexOf('1.0') !== -1
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show) {
        this.$store.dispatch('getAllClusters').then(() => {
          this.newCluster = find(this.$store.getters.allClusters, cluster => cluster.id === this.endDeviceToEdit.cluster.id)
        })
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    endDeviceToEdit () {
      if (!isEmpty(this.endDeviceToEdit)) {
        this.id = this.endDeviceToEdit.id
        // newCluster is set after getting all fleets
        this.name = this.endDeviceToEdit.name
        this.activation = this.endDeviceToEdit.activation
        this.adrEnabled = this.endDeviceToEdit.adrEnabled
        this.devNonceCounter = this.endDeviceToEdit.devNonceCounter
        this.geolocation = this.endDeviceToEdit.geolocation
        this.longitude = this.endDeviceToEdit.longitude
        this.latitude = this.endDeviceToEdit.latitude
        this.altitude = this.endDeviceToEdit.altitude
      }
    }
  },

  methods: {
    async reset () {
      this.id = ''
      this.newCluster = {}
      this.name = ''
      this.activation = ''
      this.adrEnabled = false
      this.devNonceCounter = false
      this.geolocation = ''
      this.longitude = ''
      this.latitude = ''
      this.altitude = ''

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async update () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const endDevice = pick(this, ['name', 'adrEnabled', 'geolocation', 'longitude', 'latitude', 'altitude'])
      if (this.activation === 'OTAA' && this.isLoRaWan10) endDevice.devNonceCounter = this.devNonceCounter
      if (this.newCluster.id !== this.endDeviceToEdit.cluster.id) endDevice.cluster = { id: this.newCluster.id }

      let error
      [error] = await to(this.$api.patchEndDevice(this.endDeviceToEdit, endDevice))
      if (error) return

      this.visible = false
      // Success
      eventBus.$emit('message-success', this.$t('ed-endDeviceForm:editSuccess', { endDevice: this.endDeviceToEdit.devEui }))
      // Reset form
      this.reset()
      // Refresh
      this.$emit('success')
    }
  }
}
</script>
